// Invoice list: search, status filter, totals for the current filter, and the
// row actions (open, download PDF, delete).
//
// When VERI*FACTU is in use the rows also carry where each invoice stands with
// the AEAT register, and two actions that belong to it: filing an invoice that
// was not filed, and withdrawing one that was. A filed invoice cannot be edited
// or deleted — the regulation's answer to a mistake is to withdraw and re-issue —
// so those two controls are disabled rather than left to fail at the server, with
// a tooltip saying why.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync, useDebounced, useErrorMessage } from '../lib/hooks';
import { useI18n, useT } from '../lib/i18n';
import { useSession } from '../lib/session';
import { useToast } from '../components/Toast';
import { Icon } from '../components/Icon';
import { EmailDialog } from '../components/EmailDialog';
import {
  ConfirmDialog, EmptyState, ErrorAlert, LoadingRow, Pagination, SearchInput, StatusBadge,
} from '../components/ui';
import { RecordStatusBadge } from './Verifactu';
import { formatDate, formatMoney } from '@shared/format';
import type { InvoiceStatus, InvoiceSummary } from '@shared/types';

const PER_PAGE = 20;

/** On the AEAT register: filed, or filed with warnings. */
const filed = (invoice: InvoiceSummary): boolean =>
  invoice.verifactu?.status === 'accepted' || invoice.verifactu?.status === 'warned';

/**
 * Fixed for good. A filed invoice's record states its total, so changing or
 * deleting the invoice would leave a false filing behind; the way out is to
 * withdraw it and issue a replacement.
 */
const locked = (invoice: InvoiceSummary): boolean => filed(invoice);

export function Invoices() {
  const t = useT();
  const { locale } = useI18n();
  const { money } = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  const toMessage = useErrorMessage();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<InvoiceStatus | ''>('');
  const [page, setPage] = useState(1);
  const [pendingDelete, setPendingDelete] = useState<InvoiceSummary>();
  const [pendingWithdraw, setPendingWithdraw] = useState<InvoiceSummary>();
  const [emailing, setEmailing] = useState<InvoiceSummary>();
  const [deleting, setDeleting] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  // The invoice currently being filed, so only its own button spins.
  const [filing, setFiling] = useState<number>();
  // Likewise for the paid / pending toggle.
  const [settling, setSettling] = useState<number>();

  const q = useDebounced(search);
  const { data, loading, error, reload } = useAsync(
    () => api.invoices({ q, status: status || undefined, page, perPage: PER_PAGE }),
    [q, status, page]);

  // A new search term invalidates the page number.
  const onSearch = (value: string) => { setSearch(value); setPage(1); };
  const onStatus = (value: InvoiceStatus | '') => { setStatus(value); setPage(1); };

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const { number } = await api.deleteInvoice(pendingDelete.id);
      toast.success(t('invoices.deleted', { number }));
      setPendingDelete(undefined);
      reload();
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setDeleting(false);
    }
  }

  /**
   * Flips an invoice between paid and pending without opening it. Kept two-way
   * because a filed invoice cannot be opened in the editor, so a slip here could
   * not otherwise be undone.
   */
  async function toggleStatus(invoice: InvoiceSummary) {
    const next: InvoiceStatus = invoice.status === 'paid' ? 'pending' : 'paid';
    setSettling(invoice.id);
    try {
      await api.setInvoiceStatus(invoice.id, next);
      toast.success(t(next === 'paid' ? 'invoices.markedPaid' : 'invoices.markedPending',
        { number: invoice.number }));
      reload();
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setSettling(undefined);
    }
  }

  /** Files an invoice that has no accepted record, or supersedes a rejected one. */
  async function file(invoice: InvoiceSummary) {
    setFiling(invoice.id);
    try {
      await api.fileInvoice(invoice.id);
      toast.success(t('verifactu.filed', { number: invoice.number }));
      reload();
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setFiling(undefined);
    }
  }

  async function confirmWithdraw() {
    if (!pendingWithdraw) return;
    setWithdrawing(true);
    try {
      await api.withdrawInvoice(pendingWithdraw.id);
      toast.success(t('verifactu.withdrawn', { number: pendingWithdraw.number }));
      setPendingWithdraw(undefined);
      reload();
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setWithdrawing(false);
    }
  }

  const totals = data?.totals;
  const currency = (value: number) => formatMoney(value, money);
  // Absent on every row when VERI*FACTU has never been switched on, which is
  // what keeps the column and its actions out of the way of a deployment that is
  // not under the regime.
  const showRegister = (data?.items ?? []).some((invoice) => invoice.verifactu !== undefined);

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">{t('invoices.title')}</h1>
          <p className="page-head__subtitle">{t('app.tagline')}</p>
        </div>
        <div className="page-head__actions">
          <Link to="/invoices/new" className="btn btn--primary">
            <Icon name="plus" />
            {t('invoices.new')}
          </Link>
        </div>
      </div>

      {totals && totals.count > 0 && (
        <div className="stats">
          <div className="stat">
            <div className="stat__label">{t('invoices.totals.invoices')}</div>
            <div className="stat__value">{totals.count}</div>
          </div>
          <div className="stat">
            <div className="stat__label">{t('invoices.totals.net')}</div>
            <div className="stat__value stat__value--muted">{currency(totals.subtotal)}</div>
          </div>
          <div className="stat">
            <div className="stat__label">{t('invoices.totals.tax')}</div>
            <div className="stat__value stat__value--muted">{currency(totals.taxTotal)}</div>
          </div>
          <div className="stat">
            <div className="stat__label">{t('invoices.totals.gross')}</div>
            <div className="stat__value">{currency(totals.total)}</div>
          </div>
          <div className="stat">
            <div className="stat__label">{t('invoices.totals.outstanding')}</div>
            <div className={`stat__value${totals.outstanding > 0 ? ' stat__value--warning' : ' stat__value--muted'}`}>
              {currency(totals.outstanding)}
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card__head">
          <div className="toolbar" style={{ flex: 1 }}>
            <SearchInput value={search} onChange={onSearch} placeholder={t('invoices.searchPlaceholder')} />
            <select className="select" style={{ width: 'auto' }} value={status}
              onChange={(e) => onStatus(e.target.value as InvoiceStatus | '')}
              aria-label={t('invoices.filterStatus')}>
              <option value="">{t('common.all')}</option>
              <option value="paid">{t('status.paid')}</option>
              <option value="pending">{t('status.pending')}</option>
            </select>
            {loading && data && <span className="spinner" />}
          </div>
        </div>

        {error ? (
          <div className="card__body"><ErrorAlert message={error} onRetry={reload} /></div>
        ) : loading && !data ? (
          <LoadingRow />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={q || status ? 'search' : 'inbox'}
            title={q || status ? t('common.noResults') : t('invoices.empty')}
            hint={q || status ? t('common.noResultsHint') : t('invoices.emptyHint')}
            action={!q && !status && (
              <Link to="/invoices/new" className="btn btn--primary">
                <Icon name="plus" />{t('invoices.new')}
              </Link>
            )}
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th className="shrink">{t('invoices.number')}</th>
                  <th className="shrink">{t('invoices.date')}</th>
                  <th>{t('invoices.client')}</th>
                  <th>{t('invoices.seller')}</th>
                  <th className="shrink">{t('invoice.field.paymentMethod')}</th>
                  <th className="shrink">{t('common.status')}</th>
                  {showRegister && <th className="shrink">{t('verifactu.title')}</th>}
                  <th className="num">{t('common.total')}</th>
                  <th className="shrink" />
                </tr>
              </thead>
              <tbody>
                {data?.items.map((invoice) => (
                  <tr key={invoice.id}>
                    <td className="shrink mono">{invoice.number}</td>
                    <td className="shrink nowrap">{formatDate(invoice.issuedAt, locale)}</td>
                    <td>
                      <Link to={`/invoices/${invoice.id}`} className="table__primary">{invoice.clientName}</Link>
                      <div className="table__meta truncate">
                        {invoice.quotationNumber
                          ? t('quotation.fromQuotation', { number: invoice.quotationNumber })
                          : invoice.lastEmailedAt
                            ? t('email.lastSent', { date: formatDate(invoice.lastEmailedAt, locale) })
                            : [invoice.clientPhone, invoice.clientEmail].filter(Boolean).join(' · ')}
                      </div>
                    </td>
                    <td className="table__meta">{invoice.sellerName}</td>
                    <td className="shrink table__meta nowrap">{t(`payment.${invoice.paymentMethod}`)}</td>
                    <td className="shrink"><StatusBadge status={invoice.status} /></td>
                    {showRegister && (
                      <td className="shrink">
                        {invoice.verifactu?.status
                          ? <RecordStatusBadge status={invoice.verifactu.status} />
                          : <span className="badge badge--neutral">{t('verifactu.invoiceNotFiled')}</span>}
                        {invoice.verifactu?.cancelled && (
                          <div className="table__meta">{t('verifactu.invoiceCancelled')}</div>
                        )}
                        {invoice.verifactu?.csv && (
                          <div className="table__meta mono truncate">{invoice.verifactu.csv}</div>
                        )}
                        {invoice.verifactu?.errorMessage && (
                          <div className="table__meta truncate" title={invoice.verifactu.errorMessage}>
                            {invoice.verifactu.errorCode && `${invoice.verifactu.errorCode} · `}
                            {invoice.verifactu.errorMessage}
                          </div>
                        )}
                      </td>
                    )}
                    <td className="num">
                      <div className="table__primary">{currency(invoice.total)}</div>
                      <div className="table__meta">{t('invoices.lines')}: {invoice.lineCount}</div>
                    </td>
                    <td className="shrink">
                      <div className="table__actions">
                        <button type="button"
                          className={`btn btn--ghost btn--icon${invoice.status === 'pending' ? ' btn--success' : ''}`}
                          title={invoice.status === 'paid' ? t('invoices.markPending') : t('invoices.markPaid')}
                          aria-label={invoice.status === 'paid' ? t('invoices.markPending') : t('invoices.markPaid')}
                          disabled={settling === invoice.id}
                          onClick={() => void toggleStatus(invoice)}>
                          {settling === invoice.id
                            ? <span className="spinner" />
                            : <Icon name={invoice.status === 'paid' ? 'clock' : 'check'} />}
                        </button>
                        <button type="button" className="btn btn--ghost btn--icon"
                          title={locked(invoice) ? t('verifactu.invoiceLocked') : t('common.edit')}
                          aria-label={t('common.edit')}
                          disabled={locked(invoice)}
                          onClick={() => navigate(`/invoices/${invoice.id}`)}>
                          <Icon name="edit" />
                        </button>
                        {showRegister && (filed(invoice) ? (
                          !invoice.verifactu?.cancelled && (
                            <button type="button" className="btn btn--ghost btn--icon"
                              title={t('verifactu.withdrawInvoice')}
                              aria-label={t('verifactu.withdrawInvoice')}
                              onClick={() => setPendingWithdraw(invoice)}>
                              <Icon name="x" />
                            </button>
                          )
                        ) : (
                          // A record still waiting to go is left alone: the sweep
                          // has it, and a second one would duplicate it.
                          invoice.verifactu?.status !== 'pending' && (
                            <button type="button" className="btn btn--ghost btn--icon"
                              title={t('verifactu.fileInvoice')} aria-label={t('verifactu.fileInvoice')}
                              disabled={filing === invoice.id}
                              onClick={() => void file(invoice)}>
                              {filing === invoice.id ? <span className="spinner" /> : <Icon name="shield" />}
                            </button>
                          )
                        ))}
                        <button type="button" className="btn btn--ghost btn--icon"
                          title={t('email.send')} aria-label={t('email.send')}
                          onClick={() => setEmailing(invoice)}>
                          <Icon name="mail" />
                        </button>
                        <a className="btn btn--ghost btn--icon"
                          href={api.invoicePdfUrl(invoice.id, locale)}
                          title={t('invoices.download')} aria-label={t('invoices.download')}>
                          <Icon name="download" />
                        </a>
                        <button type="button" className="btn btn--ghost btn--icon"
                          title={locked(invoice) ? t('verifactu.invoiceLockedDelete') : t('common.delete')}
                          aria-label={t('common.delete')}
                          disabled={locked(invoice)}
                          onClick={() => setPendingDelete(invoice)}>
                          <Icon name="trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data && data.items.length > 0 && (
          <div className="card__foot">
            <Pagination page={data.page} totalPages={data.totalPages} total={data.total}
              perPage={data.perPage} onChange={setPage} />
          </div>
        )}
      </div>

      {emailing && (
        <EmailDialog
          kind="invoice"
          id={emailing.id}
          documentLabel={`${t('invoices.title')} ${emailing.number} · ${emailing.clientName}`}
          onClose={() => setEmailing(undefined)}
          onSent={reload}
        />
      )}

      {pendingWithdraw && (
        <ConfirmDialog
          title={t('verifactu.withdrawInvoice')}
          message={t('verifactu.withdrawConfirm', { number: pendingWithdraw.number })}
          confirmLabel={t('verifactu.withdrawInvoice')}
          danger
          busy={withdrawing}
          onConfirm={() => void confirmWithdraw()}
          onCancel={() => setPendingWithdraw(undefined)}
        />
      )}

      {pendingDelete && (
        <ConfirmDialog
          title={t('common.delete')}
          message={t('invoices.deleteConfirm', { number: pendingDelete.number })}
          confirmLabel={t('common.delete')}
          danger
          busy={deleting}
          onConfirm={() => void confirmDelete()}
          onCancel={() => setPendingDelete(undefined)}
        />
      )}
    </>
  );
}
